import { useQuery } from '@apollo/client';
import usePartnerRequestActions from 'FeatureModules/Partner/Components/PartnerRequests/usePartnerRequestActions';
import { removeRequestFromCacheBuyerHome } from 'FeatureModules/Partner/utils/partner.utils';
import { ERequestsTableAction } from 'FeatureModules/Request/Components/Tables/PaginatedRequestsTable';
import useConfig from 'hooks/useConfig';
import useDialog from 'hooks/useDialog';
import useMessages from 'hooks/useMessages';
import {
  GET_BASE_PARTNER_COMPANY,
  GET_ALL_PARTNER_REQUESTS,
} from 'graphql/secure/queries/partner.queries';
import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ECompanyStatus } from 'FeatureModules/Auth/types/company.types';
import {
  IBasePartnerCompany,
  IPartnerRequest,
  TPartnerRequestStatus,
} from 'FeatureModules/Partner/types/partner.types';
import { IGraphQlConnection } from 'types/core.types';
import { useQueryParam } from 'use-query-params';

const useRequests = () => {
  const { id } = useParams<{ id: string | undefined }>();
  const navigate = useNavigate();
  const { updatePartnerRequest } = usePartnerRequestActions();
  const { setErrorMessage } = useMessages();
  const { openDialog } = useDialog();
  const { appQueryParams } = useConfig();
  const { data, loading, error } = useQuery<{
    partnerRequests: IGraphQlConnection<IPartnerRequest>;
  }>(GET_ALL_PARTNER_REQUESTS, {
    variables: {
      filters: {
        ...(id ? { requestedToCompanyIds: [id] } : {}),
        requestStatus: ['REQUESTED', 'PROVIDED', 'PROVIDED_COMPLETED', 'DENIED', 'EDITED'],
      },
      orderBy: { orderByField: 'requestedTimestamp', orderByMethod: 'ASC' },
    },
  });
  const { data: partnerData, loading: partnerLoading } = useQuery<{
    partner: IBasePartnerCompany;
  }>(GET_BASE_PARTNER_COMPANY, {
    skip: !id,
    variables: {
      id,
    },
  });

  const partner: IBasePartnerCompany | undefined = partnerData?.partner;
  const isPartnerRegistered = partner?.status === ECompanyStatus.REGISTERED;

  const [searchTerm = '', setSearchTerm] = useQueryParam<string>(appQueryParams.query);

  const handleUpdateRequest = (requestId: string, requestStatus: TPartnerRequestStatus) => {
    updatePartnerRequest({
      variables: {
        id: requestId as string,
        input: {
          requestStatus: requestStatus,
        },
      },
      onError: () =>
        setErrorMessage('Something went wrong finishing the request. Please try again later.'),
      update: (cache, { data }) => {
        if (data) {
          removeRequestFromCacheBuyerHome(cache, data.updatePartnerRequest.partnerRequest);
        }
      },
    });
  };

  const handleAcceptRequest = (requestId: string) => {
    openDialog({
      type: 'ALERT',
      props: {
        title: 'Accept request',
        submitText: 'Accept and close request',
        text: `Are you sure you want to accept this request? The request will be closed and considered completed.`,
        onSubmit: () => handleUpdateRequest(requestId, 'PROVIDED_COMPLETED'),
        onCancel: () => undefined,
      },
    });
  };

  const handleReviewAndApprove = (request: IPartnerRequest) => {
    openDialog({
      type: 'REVIEW_DOCUMENT_REQUEST',
      props: {
        request: request,
      },
    });
  };

  const handleRequestClick = (requestId: string) => {
    navigate(`/partners/${id}/requests/${requestId}`);
  };

  const handleActionClick = (requestId: string, action: ERequestsTableAction) => {
    switch (action) {
      case ERequestsTableAction.MANAGE:
        const request = allRequests?.find(request => request.id === requestId);
        if (!request) {
          console.error('No request found');
          return;
        }
        openDialog({
          type: 'MANAGE_REQUEST',
          props: { request },
        });
        break;
      case ERequestsTableAction.DISMISS:
        openDialog({
          type: 'DENY_REQUEST',
          props: { requestId },
        });
        break;
      case ERequestsTableAction.SEE_DETAILS: {
        const request = allRequests?.find(request => request.id === requestId);
        if (!request) {
          console.error('No request found');
          return;
        }
        openDialog({ type: 'REQUEST_DETAILS', props: { request } });
        break;
      }
      case ERequestsTableAction.ACCEPT:
        handleAcceptRequest(requestId);
        break;
      case ERequestsTableAction.ADD_CLAIM:
        openDialog({ type: 'CREATE_IMPACT_CLAIM' });
        break;
      case ERequestsTableAction.DISMISS_EDIT: {
        const request = allRequests?.find(request => request.id === requestId);
        if (!request) {
          console.error('No request found');
          return;
        }
        openDialog({
          type: 'PARTNER_REQUEST',
          props: { requestedToCompany: request?.requestedToCompany, initialRequest: request },
        });
        break;
      }
      case ERequestsTableAction.EDIT: {
        const request = allRequests?.find(request => request.id === requestId);
        if (!request) {
          console.error('No request found');
          return;
        }
        openDialog({
          type: 'PARTNER_REQUEST',
          props: { requestedToCompany: request?.requestedToCompany, initialRequest: request },
        });
        break;
      }
      case ERequestsTableAction.REVIEW: {
        const request = allRequests?.find(request => request.id === requestId);
        if (!request) {
          console.error('No request found');
          return;
        }
        handleReviewAndApprove(request);
        break;
      }
      case ERequestsTableAction.DELETE:
        openDialog({
          type: 'ALERT',
          props: {
            title: 'Delete request',
            submitText: 'Delete request',
            text: `Are you sure you want to delete this request?`,
            onSubmit: () => handleUpdateRequest(requestId, 'DELETED'),
            onCancel: () => undefined,
          },
        });
        break;
      default:
        console.error('Unknown action', action);
    }
  };

  /**
   * Route to main overview and open create new request dialog
   */
  const handleNewRequestClick = () => {
    openDialog({
      type: 'PARTNER_REQUEST',
      props: {
        requestedToCompany: partner,
      },
    });
  };

  const allRequests = useMemo(() => {
    // use removeGraphConnections
    const nodeDocuments = [...(data?.partnerRequests?.edges || [])];
    // Last items as first
    // Denied completed and deleted requests are not shown
    const requestedDocuments = nodeDocuments
      .map(item => item.node)
      .filter(
        ({ requestStatus }) => requestStatus !== 'DENIED_COMPLETED' && requestStatus !== 'DELETED'
      )
      .reverse();

    return requestedDocuments;
  }, [data]);

  /**
   * Filter requests by search term
   * By default the all requests without the fulfilled once are shown
   */
  const filteredRequests = useMemo(() => {
    if (searchTerm?.length) {
      return allRequests.filter(({ requestTitle }) =>
        requestTitle.toLowerCase().includes(searchTerm.toLowerCase())
      );
    } else {
      return allRequests.filter(({ requestStatus }) => requestStatus !== 'PROVIDED_COMPLETED');
    }
  }, [allRequests, searchTerm]);

  const fulfilledRequests = useMemo(
    () => allRequests.filter(({ requestStatus }) => requestStatus === 'PROVIDED_COMPLETED'),
    [allRequests]
  );

  return {
    error,
    partner,
    // If partner id is defined, wait for both partner and requests to be loaded
    loading: id ? loading || partnerLoading : loading,
    allRequests,
    filteredRequests,
    fulfilledRequests,
    searchTerm,
    isPartnerRegistered,
    setSearchTerm,
    handleRequestClick,
    handleActionClick,
    handleNewRequestClick,
  };
};

export default useRequests;
